// src/components/Lms/Components/Catalogos/ProgresoEstudiantes/ProgresoEstudiantesCatalog.jsx
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { ENDPOINTS as LMS_ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsLMS';
import { Models as SecurityModels } from '../../../../Seguridad/Utils/ModelsSeguridad';
import { moduloNormalize } from '../../../Objetos/ModuloModel';
import { leccionesNormalize } from '../../../Objetos/LeccionesModel';
import { getSessionUserId } from '../../../../../utils/SessionUser';
import { API_BASE } from '../../../../../api';
import AlertDialog from '../../Shared/AlertDialog';

const nombreTabla = "Progreso de Estudiantes";

export default function ProgresoEstudiantesCatalog() {
    const [loading, setLoading] = useState(false);
    const [courses, setCourses] = useState([]);
    const [selectedCourseId, setSelectedCourseId] = useState(0); // 0 = TODOS
    const [rows, setRows] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [idInstructor, setIdInstructor] = useState(null);
    const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

    const triggerAlert = (message, variant = 'error', title = 'Error') => {
        setAlertConfig({ open: true, message, variant, title });
    };

    // Datos del curso base (para saber total de items)
    const [courseStructure, setCourseStructure] = useState(null);

    const currentCourse = useMemo(
        () => courses.find((c) => c.idCurso === selectedCourseId) || null,
        [courses, selectedCourseId]
    );

    const filteredRows = useMemo(() => {
        if (!searchTerm.trim()) return rows;
        const term = searchTerm.toLowerCase();
        return rows.filter(r =>
            (r.nombreCompleto || '').toLowerCase().includes(term) ||
            (r.nombreCurso || '').toLowerCase().includes(term)
        );
    }, [rows, searchTerm]);

    // 1. Cargar ID de Instructor desde el usuario en sesión
    useEffect(() => {
        const loadInstructorId = async () => {
            try {
                const idUsuario = getSessionUserId();
                if (!idUsuario) return;

                const res = await axios.get(LMS_ENDPOINTS.instructorByUsuario(idUsuario));
                const instructor = res.data;
                const instructorId = instructor?.idInstructor ?? instructor?.IdInstructor;

                if (instructorId) {
                    setIdInstructor(instructorId);
                }
            } catch (error) {
                console.error('[ProgresoEstudiantes] Error al cargar identificación de instructor:', error);
            }
        };

        loadInstructorId();
    }, []);

    // 2. Cargar lista de cursos filtrados por instructor
    useEffect(() => {
        const loadCourses = async () => {
            if (!idInstructor) {
                console.log('[ProgresoEstudiantes] Esperando idInstructor...');
                return;
            }

            setLoading(true);
            try {
                const res = await axios.get(ENDPOINTS.cursos);
                const list = Array.isArray(res.data) ? res.data : [];

                // Filtrar solo cursos del instructor
                const normalized = list
                    .map((c) => ({
                        idCurso: c.idCurso ?? c.IdCurso,
                        nombre: c.nombre ?? c.Nombre,
                        activo: c.activo ?? c.Activo,
                        idInstructor: c.idInstructor ?? c.IdInstructor,
                        terminado: c.terminado ?? c.Terminado,
                    }))
                    .filter((c) =>
                        Number.isFinite(Number(c.idCurso)) &&
                        Number(c.idInstructor) === Number(idInstructor) &&
                        (c.activo === true || String(c.activo).toLowerCase() === 'true') &&
                        (c.terminado === true || String(c.terminado).toLowerCase() === 'true')
                    );

                console.log('[ProgresoEstudiantes] Cursos del instructor:', normalized.length);

                setCourses(normalized);
                if (normalized.length && selectedCourseId == null) {
                    setSelectedCourseId(Number(normalized[0].idCurso));
                }
            } catch (e) {
                console.error('[ProgresoEstudiantes] Error al cargar cursos:', e);
            } finally {
                setLoading(false);
            }
        };

        loadCourses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idInstructor]);

    // 3. Cargar datos de progreso optimizados
    useEffect(() => {
        const loadData = async () => {
            if (!idInstructor) return;

            // Si selectedCourseId es null (inicio), no cargamos nada hasta que se defina (usualmente 0 o el primer curso)
            if (selectedCourseId === null) return;

            setLoading(true);
            try {
                // Usamos el nuevo endpoint optimizado que devuelve todo procesado desde SQL
                const res = await axios.get(LMS_ENDPOINTS.estudianteCursoProgresoDetalle(idInstructor, selectedCourseId));
                const data = res.data || [];

                // Mapeamos al formato que ya usa la tabla para no romper el renderizado
                const mappedRows = data.map(item => ({
                    idEstudiante: item.idEstudiante ?? item.IdEstudiante,
                    idCurso: item.idCurso ?? item.IdCurso,
                    nombreCompleto: item.nombreCompleto ?? item.NombreCompleto,
                    nombreCurso: item.nombreCurso ?? item.NombreCurso,
                    promedioGeneral: item.promedioGeneral ?? item.PromedioGeneral,
                    progresoPorcentaje: item.progresoPorcentaje ?? item.ProgresoPorcentaje,
                    cuestionariosResueltos: item.cuestionariosResueltos ?? item.CuestionariosResueltos,
                    totalCuestionarios: item.totalCuestionarios ?? item.TotalCuestionarios,
                    examenesResueltos: item.examenesResueltos ?? item.ExamenesResueltos,
                    totalExamenes: item.totalExamenes ?? item.TotalExamenes,
                    certificadoPedido: item.certificadoPedido ?? item.CertificadoPedido ?? false,
                    certificadoAtendido: item.certificadoAtendido ?? item.CertificadoAtendido ?? false
                }));

                setRows(mappedRows);
            } catch (e) {
                console.error('[ProgresoEstudiantes] Error al cargar progreso optimizado:', e);
                setRows([]);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [idInstructor, selectedCourseId]);

    // Exportar a Excel
    const downloadExcel = () => {
        if (!rows || rows.length === 0) {
            triggerAlert("No hay datos para exportar.", "warning", "Atención");
            return;
        }

        const headers = [
            "Alumno",
            "Curso",
            "Calificación Actual",
            "Progreso Curso",
            "Cuestionarios Aprobados",
            "Total Cuestionarios",
            "Exámenes Aprobados",
            "Total Exámenes",
            "Certificado Pedido",
            "Certificado Atendido"
        ];

        const csvRows = [
            headers.join(","),
            ...filteredRows.map((r) => {
                const prog = Math.round(r.progresoPorcentaje);
                const calif = r.promedioGeneral != null ? Math.round(r.promedioGeneral) : "";

                return [
                    `"${r.nombreCompleto || ''}"`,
                    `"${r.nombreCurso || ''}"`,
                    `"${calif}"`,
                    `"${prog}%"`,
                    `"${r.cuestionariosResueltos}"`,
                    `"${r.totalCuestionarios}"`,
                    `"${r.examenesResueltos}"`,
                    `"${r.totalExamenes}"`,
                    `"${r.certificadoPedido ? 'SÍ' : 'NO'}"`,
                    `"${r.certificadoAtendido ? 'SÍ' : 'NO'}"`
                ].join(",");
            }),
        ];

        const csvString = "\uFEFF" + csvRows.join("\n");
        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Progreso_${currentCourse?.nombre || 'General'}_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="panel-sigma-main">
            <div className="roles-shell">
                <div className="roles-topbar" style={{ flexWrap: 'wrap', gap: '1rem' }}>
                    <h2 className="roles-title" style={{ margin: 0 }}>{nombreTabla}</h2>

                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
                        <div className="roles-search-box" style={{ width: '300px' }}>
                            <input
                                type="text"
                                className="roles-input"
                                placeholder="Buscar por alumno o curso..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '0.55rem 1rem',
                                    borderRadius: '10px',
                                    border: '1px solid #cbd5e1',
                                    fontSize: '0.9rem'
                                }}
                            />
                        </div>

                        <select
                            className="roles-select"
                            value={selectedCourseId || ''}
                            onChange={(e) => setSelectedCourseId(Number(e.target.value))}
                            style={{
                                minWidth: '280px',
                                padding: '0.55rem 0.9rem',
                                borderRadius: '10px',
                                border: '1px solid #cbd5e1',
                                background: '#ffffff',
                                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
                                fontWeight: 600,
                                color: '#0f172a',
                            }}
                        >
                            <option value={0}>TODOS LOS CURSOS</option>
                            {courses.map((c) => (
                                <option key={c.idCurso} value={c.idCurso}>
                                    {c.nombre}
                                </option>
                            ))}
                        </select>

                        <button className="roles-btn" onClick={downloadExcel} title="Descargar Excel">
                            📥 Reporte
                        </button>
                    </div>
                </div>

                {currentCourse && (
                    <div style={{ padding: '0.75rem 1.25rem', background: '#f8fafc', borderRadius: '12px', marginBottom: '1.25rem', border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <h3 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', color: '#1e293b' }}>{currentCourse.nombre}</h3>
                            <p style={{ margin: 0, color: '#64748b', fontSize: '0.85rem', fontWeight: 500 }}>
                                Detalle de progreso para {filteredRows.length} estudiantes
                            </p>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="catalog-loading">Cargando progreso...</div>
                ) : (
                    <div className="roles-table-wrap">
                        <div className="roles-table-scroller">
                            <table className="roles-table">
                                <thead>
                                    <tr>
                                        <th style={{ minWidth: '220px' }}>Alumno</th>
                                        <th style={{ minWidth: '200px' }}>Curso</th>
                                        <th style={{ minWidth: '110px', textAlign: 'center' }}>Calif. Actual</th>
                                        <th style={{ minWidth: '150px', textAlign: 'center' }}>Progreso Curso</th>
                                        <th style={{ minWidth: '110px', textAlign: 'center' }}>Cuestionarios</th>
                                        <th style={{ minWidth: '110px', textAlign: 'center' }}>Exámenes</th>
                                        <th style={{ minWidth: '130px', textAlign: 'center' }}>Certificado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRows.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
                                                {searchTerm ? 'No se encontraron resultados para tu búsqueda.' : 'No hay alumnos o datos disponibles.'}
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredRows.map((r) => {
                                            const prog = Math.round(r.progresoPorcentaje || 0);
                                            const calif = (r.promedioGeneral == null || Number.isNaN(Number(r.promedioGeneral)))
                                                ? null
                                                : Math.round(Number(r.promedioGeneral));

                                            const califBg = calif == null ? 'transparent' : calif < 60 ? '#fee2e2' : calif < 80 ? '#fef3c7' : '#dcfce7';
                                            const califColor = calif == null ? '#0f172a' : calif < 60 ? '#991b1b' : calif < 80 ? '#854d0e' : '#166534';
                                            const progressColor = prog < 30 ? '#ef4444' : prog < 70 ? '#f59e0b' : '#10b981';

                                            return (
                                                <tr key={`${r.idCurso}-${r.idEstudiante}`}>
                                                    <td>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                            <div style={{
                                                                width: '32px',
                                                                height: '32px',
                                                                borderRadius: '50%',
                                                                background: '#3b82f6',
                                                                color: '#fff',
                                                                display: 'grid',
                                                                placeItems: 'center',
                                                                fontSize: '0.85rem',
                                                                fontWeight: '700',
                                                                flexShrink: 0
                                                            }}>
                                                                {(r.nombreCompleto || 'E').charAt(0)}
                                                            </div>
                                                            <div>
                                                                <div style={{ fontWeight: '600', color: '#1e293b' }}>{r.nombreCompleto || '—'}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div style={{ fontWeight: '600', color: '#334155', fontSize: '0.9rem' }}>{r.nombreCurso || '—'}</div>
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        {calif == null ? "—" : (
                                                            <span style={{
                                                                padding: '0.25rem 0.6rem',
                                                                borderRadius: '6px',
                                                                background: califBg,
                                                                color: califColor,
                                                                fontSize: '0.9rem',
                                                                fontWeight: '700'
                                                            }}>
                                                                {calif}%
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <span style={{
                                                            padding: '0.25rem 0.6rem',
                                                            borderRadius: '6px',
                                                            background: prog < 30 ? '#fee2e2' : prog < 70 ? '#fef3c7' : '#dcfce7',
                                                            color: prog < 30 ? '#991b1b' : prog < 70 ? '#854d0e' : '#166534',
                                                            fontSize: '0.9rem',
                                                            fontWeight: '700'
                                                        }}>
                                                            {prog}%
                                                        </span>
                                                    </td>
                                                    <td style={{ textAlign: 'center', color: '#475569', fontWeight: '500' }}>
                                                        {r.cuestionariosResueltos} / {r.totalCuestionarios}
                                                    </td>
                                                    <td style={{ textAlign: 'center', color: '#475569', fontWeight: '500' }}>
                                                        {r.examenesResueltos} / {r.totalExamenes}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <span style={{
                                                            padding: '2px 10px',
                                                            borderRadius: '12px',
                                                            fontSize: '0.75rem',
                                                            fontWeight: '700',
                                                            backgroundColor: r.certificadoAtendido ? '#dcfce7' : '#f1f5f9',
                                                            color: r.certificadoAtendido ? '#166534' : '#64748b',
                                                            border: r.certificadoAtendido ? '1px solid #bbf7d0' : '1px solid #e2e8f0'
                                                        }}>
                                                            {r.certificadoAtendido ? 'SÍ' : 'NO'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            <AlertDialog
                open={alertConfig.open}
                title={alertConfig.title}
                message={alertConfig.message}
                variant={alertConfig.variant}
                onClose={() => setAlertConfig({ ...alertConfig, open: false })}
            />
        </div>
    );
}
